/**
 * Neue Massnahme anlegen – eigenständige Tab-Seite
 * 2-Spalten-Layout: Formular links, Dokumentenvorschau rechts
 * KI-Autofill mit Duplikat-Erkennung
 */
import { useState, useEffect, useCallback } from 'react';
import { Briefcase, Check, Loader2, Search, Shield, X, Sparkles, FileText, Eye, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useTabs } from '@/contexts/TabContext';
import { emitEntityCreated, ENTITY_TYPES } from '@/lib/tabEvents';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

/** Farbmarkierung für KI-befüllte Felder */
function fieldStyle(fieldKey, kiStatus) {
  if (!kiStatus) return '';
  if (kiStatus[fieldKey] === 'found') return 'ring-2 ring-orange-400 bg-orange-50';
  if (kiStatus[fieldKey] === 'not_found') return 'ring-2 ring-red-300 bg-red-50';
  return '';
}

/** Mini-Dokumentenvorschau */
function DokumentMiniVorschau({ url, name, dateityp }) {
  if (!url) return null;
  const isPdf = dateityp?.toLowerCase().endsWith('.pdf') || name?.toLowerCase().endsWith('.pdf');
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(name || '') || /\.(jpg|jpeg|png|gif|webp)$/i.test(dateityp || '');

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <p className="text-xs font-medium text-slate-600 flex items-center gap-1.5 truncate">
          <Eye className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{name || 'Dokument'}</span>
        </p>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
          onClick={() => window.open(url, '_blank')}>
          <Eye className="w-3 h-3 mr-1" /> Groß
        </Button>
      </div>
      <div className="flex-1 overflow-hidden bg-slate-100 min-h-[300px]">
        {isPdf ? (
          <object data={url} type="application/pdf" className="w-full h-full border-0" style={{ minHeight: '100%' }}>
            <iframe src={`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`}
              className="w-full h-full border-0" title="Vorschau" />
          </object>
        ) : isImage ? (
          <div className="w-full h-full flex items-center justify-center p-2">
            <img src={url} alt="Vorschau" className="max-w-full max-h-full object-contain rounded" />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400">
            <div className="text-center">
              <FileText className="w-8 h-8 mx-auto mb-1" />
              <p className="text-xs">Vorschau nicht verfügbar</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function NeueMassnahmePage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};

  const [form, setForm] = useState({
    typ: '', bezeichnung: '', leistungsgeber_id: '', aktenzeichen: '', notizen: '',
    kv_art: '', kv_beitragszahler: '', zuzahlungsbefreiung_status: '',
    zuzahlungsbefreiung_gueltig_bis: '', zuzahlungsbefreiung_art: '',
    chroniker_status: false, belastungsgrenze_erreicht_am: '',
    // Schwerbehinderung
    sb_feststellungsbescheid: false, sb_ausweis_vorhanden: false,
    sb_gdb: '', sb_merkzeichen: '', sb_ausweis_status: '',
    sb_ausweis_gueltig_bis: '', sb_zustaendige_behoerde: '',
    sb_aenderungsbedarf: '', sb_nachteilsausgleiche: '',
    // Zuzahlungsbefreiung
    zb_kk_bekannt: false, zb_kk_name: '', zb_antrag_gestellt: false,
    zb_befreiung_bewilligt: false, zb_befreiungszeitraum_bekannt: false,
    zb_befreiung_von: '', zb_befreiung_bis: '',
    zb_belastungsgrenze: '', zb_chronisch: '', zb_nachweise: '', zb_pruefbedarf: true,
  });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [massnahmenkatalog, setMassnahmenkatalog] = useState([]);
  const [jurPersonen, setJurPersonen] = useState([]);
  const [kiLoading, setKiLoading] = useState(false);
  const [kiStatus, setKiStatus] = useState(null);
  const [kiStats, setKiStats] = useState(null);
  const [duplikatWarnung, setDuplikatWarnung] = useState(null);

  const hasDocument = !!params.dokumentVorschauUrl;
  const canUseKi = !!(params.ocrText?.length > 10 || params.dokumentId);

  useEffect(() => {
    Promise.all([
      betreuungAPI.listMassnahmenkatalog().catch(() => []),
      betreuungAPI.listJuristischePersonen().catch(() => []),
    ]).then(([katalog, jp]) => {
      setMassnahmenkatalog(Array.isArray(katalog) ? katalog : []);
      setJurPersonen(Array.isArray(jp) ? jp : []);
    });
  }, []);

  const filteredKatalog = massnahmenkatalog.filter(m =>
    !searchQuery || m.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Erzeugt einen konsistenten Wert aus dem Katalogeintrag (typ falls vorhanden, sonst name als slug)
  const katalogValue = (m) => m.typ || m.name?.toLowerCase().replace(/[^a-zäöüß0-9]/g, '_').replace(/_+/g, '_') || `item-${m.id}`;
  const isKrankenversicherung = form.typ?.toLowerCase().includes('krankenversicherung');
  const isSchwerbehinderung = form.typ?.toLowerCase().includes('schwerbehinderung');
  const isZuzahlungsbefreiung = form.typ?.toLowerCase().includes('zuzahlungsbefreiung');

  /** KI-Autofill: Extrahiert Maßnahmendaten + Duplikat-Check */
  const handleKiAutofill = async () => {
    if (!canUseKi) { toast.error('Kein Dokument vorhanden'); return; }

    setKiLoading(true);
    setKiStatus(null);
    setKiStats(null);
    setDuplikatWarnung(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/wiki/extract-massnahme-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ocr_text: params.ocrText || null,
          dokument_id: params.dokumentId || null,
          klient_id: params.klientId || null,
          klient_name: params.klientName || null,
        }),
      });
      const data = await res.json();

      if (data.success && data.daten) {
        setForm(prev => {
          const next = { ...prev };
          // Maßnahmentyp: KI-Wert gegen Katalog matchen
          if (data.daten.massnahmentyp && data.felder_status.massnahmentyp === 'found') {
            const kiTyp = data.daten.massnahmentyp.toLowerCase();
            const match = massnahmenkatalog.find(m => {
              const val = katalogValue(m);
              return val === kiTyp || m.name?.toLowerCase() === kiTyp
                || m.name?.toLowerCase().includes(kiTyp) || kiTyp.includes(m.name?.toLowerCase() || '');
            });
            next.typ = match ? katalogValue(match) : kiTyp;
          }
          if (data.daten.aktenzeichen && data.felder_status.aktenzeichen === 'found') {
            next.aktenzeichen = data.daten.aktenzeichen;
          }
          if (data.daten.notiz && data.felder_status.notiz === 'found') {
            next.notizen = data.daten.notiz;
          }
          // Leistungsgeber: Versuche per Name zu matchen
          if (data.daten.leistungsgeber_name) {
            const match = jurPersonen.find(j =>
              j.name?.toLowerCase().includes(data.daten.leistungsgeber_name.toLowerCase()) ||
              data.daten.leistungsgeber_name.toLowerCase().includes(j.name?.toLowerCase() || '')
            );
            if (match) {
              next.leistungsgeber_id = match.id;
            }
          }
          return next;
        });

        setKiStatus(data.felder_status);
        setKiStats({ gefunden: data.anzahl_gefunden, nicht_gefunden: data.anzahl_nicht_gefunden });

        // Duplikat-Warnung
        if (data.duplikat_warnung?.ist_duplikat) {
          setDuplikatWarnung(data.duplikat_warnung);
        }

        toast.success(`Zeo: ${data.anzahl_gefunden} Felder gefunden, ${data.anzahl_nicht_gefunden} nicht gefunden`);
      } else {
        toast.error(data.error || 'KI-Extraktion fehlgeschlagen');
      }
    } catch (e) {
      console.error('KI-Autofill Fehler:', e);
      toast.error('Fehler bei der KI-Analyse');
    } finally {
      setKiLoading(false);
    }
  };

  /** Feld-Update: KI-Status für dieses Feld zurücksetzen */
  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (kiStatus?.[key]) {
      setKiStatus(prev => ({ ...prev, [key]: undefined }));
    }
  };

  const handleSave = async () => {
    if (!form.typ) { toast.error('Massnahmentyp ist erforderlich'); return; }
    setSaving(true);
    try {
      const massnahmeData = {
        person_id: params.klientId || null,
        typ: form.typ,
        bezeichnung: form.bezeichnung || null,
        leistungsgeber_id: form.leistungsgeber_id || null,
        aktenzeichen: form.aktenzeichen || null,
        notizen: form.notizen || null,
        status: 'aktiv'
      };
      if (isKrankenversicherung) {
        massnahmeData.kv_art = form.kv_art || null;
        massnahmeData.kv_beitragszahler = form.kv_beitragszahler || null;
        massnahmeData.zuzahlungsbefreiung_status = form.zuzahlungsbefreiung_status || null;
        massnahmeData.zuzahlungsbefreiung_gueltig_bis = form.zuzahlungsbefreiung_gueltig_bis || null;
        massnahmeData.zuzahlungsbefreiung_art = form.zuzahlungsbefreiung_art || null;
        massnahmeData.chroniker_status = form.chroniker_status || false;
        massnahmeData.belastungsgrenze_erreicht_am = form.belastungsgrenze_erreicht_am || null;
      }
      if (isSchwerbehinderung) {
        massnahmeData.feststellungsbescheid_vorhanden = form.sb_feststellungsbescheid || false;
        massnahmeData.schwerbehindertenausweis_vorhanden = form.sb_ausweis_vorhanden || false;
        massnahmeData.gdb = form.sb_gdb ? parseInt(form.sb_gdb) : null;
        massnahmeData.merkzeichen = form.sb_merkzeichen ? form.sb_merkzeichen.split(',').map(m => m.trim()).filter(Boolean) : [];
        massnahmeData.ausweis_status = form.sb_ausweis_status || null;
        massnahmeData.ausweis_gueltig_bis = form.sb_ausweis_gueltig_bis || null;
        massnahmeData.zustaendige_behoerde = form.sb_zustaendige_behoerde || null;
        massnahmeData.aenderungsbedarf = form.sb_aenderungsbedarf || null;
        massnahmeData.ungenutzte_nachteilsausgleiche = form.sb_nachteilsausgleiche ? form.sb_nachteilsausgleiche.split(',').map(n => n.trim()).filter(Boolean) : [];
      }
      if (isZuzahlungsbefreiung) {
        massnahmeData.krankenkasse_bekannt = form.zb_kk_bekannt || false;
        massnahmeData.krankenkasse_name = form.zb_kk_name || null;
        massnahmeData.antrag_gestellt = form.zb_antrag_gestellt || false;
        massnahmeData.befreiung_bewilligt = form.zb_befreiung_bewilligt || false;
        massnahmeData.befreiungszeitraum_bekannt = form.zb_befreiungszeitraum_bekannt || false;
        massnahmeData.befreiung_von = form.zb_befreiung_von || null;
        massnahmeData.befreiung_bis = form.zb_befreiung_bis || null;
        massnahmeData.belastungsgrenze_erreicht = form.zb_belastungsgrenze || null;
        massnahmeData.chronische_erkrankung = form.zb_chronisch || null;
        massnahmeData.nachweise_vollstaendig = form.zb_nachweise || null;
        massnahmeData.weiterer_pruefbedarf = form.zb_pruefbedarf || false;
      }
      const result = await betreuungAPI.createMassnahme(massnahmeData);
      const typLabel = massnahmenkatalog.find(m => m.typ === form.typ)?.name || form.typ;
      toast.success(`Massnahme "${typLabel}" angelegt`);
      emitEntityCreated(ENTITY_TYPES.MASSNAHME, result);
      closeTab(activeTab.id);
    } catch (e) {
      console.error('Fehler:', e);
      toast.error('Fehler beim Anlegen');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = useCallback(() => {
    closeTab(activeTab.id);
  }, [closeTab, activeTab?.id]);

  return (
    <div className={`flex gap-4 ${hasDocument ? 'max-w-5xl' : 'max-w-xl'} mx-auto`} data-testid="neue-massnahme-page">
      {/* Linke Spalte: Formular */}
      <div className={hasDocument ? 'flex-1 min-w-0' : 'w-full'}>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Briefcase className="w-5 h-5 text-purple-600" />
                Neue Massnahme anlegen
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={handleClose} data-testid="close-neue-massnahme">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <CardDescription>
              {params.klientName
                ? `Massnahme für ${params.klientName}`
                : 'Verknüpfen Sie eine Massnahme/Leistung mit dem Betreuten'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* KI-Autofill Button */}
            {canUseKi && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-indigo-50 border border-indigo-100">
                <Sparkles className="w-4 h-4 text-indigo-500 shrink-0" />
                <p className="text-xs text-indigo-700 flex-1">
                  Zeo kann Maßnahmedaten aus dem Dokument extrahieren und auf Duplikate prüfen.
                </p>
                <Button
                  size="sm" variant="outline"
                  onClick={handleKiAutofill}
                  disabled={kiLoading}
                  className="shrink-0 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                  data-testid="ki-autofill-massnahme-btn"
                >
                  {kiLoading ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analysiere...</>
                  ) : (
                    <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> KI-Autofill</>
                  )}
                </Button>
              </div>
            )}

            {/* Duplikat-Warnung */}
            {duplikatWarnung && (
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="duplikat-warnung">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-800">Mögliches Duplikat erkannt</p>
                  <p className="text-xs text-amber-700 mt-0.5">{duplikatWarnung.erklaerung}</p>
                  {duplikatWarnung.bestehende_massnahme_az && (
                    <p className="text-xs text-amber-600 mt-1">
                      Bestehend: {duplikatWarnung.bestehende_massnahme_typ} (AZ: {duplikatWarnung.bestehende_massnahme_az})
                    </p>
                  )}
                </div>
                <Button size="sm" variant="ghost" className="shrink-0 text-amber-600 h-6 px-2 text-xs"
                  onClick={() => setDuplikatWarnung(null)}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}

            {/* KI-Status Legende */}
            {kiStatus && kiStats && (
              <div className="flex items-center gap-4 text-xs px-1">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-orange-400" />
                  <span className="text-slate-600">Von KI gefunden ({kiStats.gefunden})</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-red-300" />
                  <span className="text-slate-600">Nicht gefunden ({kiStats.nicht_gefunden})</span>
                </span>
              </div>
            )}

            {/* Massnahmentyp */}
            <div>
              <Label>Massnahmentyp *</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input className="pl-8 mb-2" placeholder="Suchen..."
                  value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              </div>
              <Select value={form.typ || 'none'} onValueChange={v => updateField('typ', v === 'none' ? '' : v)}>
                <SelectTrigger className={fieldStyle('massnahmentyp', kiStatus)}>
                  <SelectValue placeholder="Massnahmentyp auswählen..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="none">-- Typ wählen --</SelectItem>
                  {filteredKatalog.map((m, idx) => (
                    <SelectItem key={`${katalogValue(m)}-${idx}`} value={katalogValue(m)}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Leistungsgeber */}
            <div>
              <Label>Leistungsgeber (Institution)</Label>
              <Select value={form.leistungsgeber_id || 'none'} onValueChange={v => updateField('leistungsgeber_id', v === 'none' ? '' : v)}>
                <SelectTrigger className={`mt-1 ${fieldStyle('leistungsgeber_name', kiStatus)}`}>
                  <SelectValue placeholder="Institution auswählen..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="none">-- Keine --</SelectItem>
                  {jurPersonen.map(j => (
                    <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Aktenzeichen */}
            <div>
              <Label>Aktenzeichen / Kundennummer</Label>
              <Input className={`mt-1 ${fieldStyle('aktenzeichen', kiStatus)}`} value={form.aktenzeichen}
                onChange={e => updateField('aktenzeichen', e.target.value)}
                placeholder="z.B. 123456789" />
            </div>

            {/* Krankenversicherung Details */}
            {isKrankenversicherung && (
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Krankenversicherung Details
                </h4>
                <div>
                  <Label>Art der Krankenversicherung</Label>
                  <Select value={form.kv_art || 'none'} onValueChange={v => setForm(p => ({ ...p, kv_art: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Art auswählen..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Auswählen --</SelectItem>
                      <SelectItem value="gkv">GKV - Gesetzliche Krankenversicherung</SelectItem>
                      <SelectItem value="fgkv">FGKV - Freiwillig gesetzlich versichert</SelectItem>
                      <SelectItem value="pkv">PKV - Privat krankenversichert</SelectItem>
                      <SelectItem value="familienversicherung">Familienversicherung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Beitragszahler (Träger)</Label>
                  <Select value={form.kv_beitragszahler || 'none'} onValueChange={v => setForm(p => ({ ...p, kv_beitragszahler: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Wer zahlt?" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Auswählen --</SelectItem>
                      <SelectItem value="selbstzahler">Selbstzahler</SelectItem>
                      <SelectItem value="arbeitgeber">Arbeitgeber</SelectItem>
                      <SelectItem value="rentenversicherung">Rentenversicherung (DRV)</SelectItem>
                      <SelectItem value="jobcenter">Jobcenter / Sozialamt</SelectItem>
                      <SelectItem value="familienangehoeriger">Familienangehöriger</SelectItem>
                      <SelectItem value="pflegekasse">Pflegekasse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
                  <h5 className="font-medium text-sm text-blue-700">Zuzahlungsbefreiung</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={form.zuzahlungsbefreiung_status || 'none'} onValueChange={v => setForm(p => ({ ...p, zuzahlungsbefreiung_status: v === 'none' ? '' : v }))}>
                        <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Status..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Status --</SelectItem>
                          <SelectItem value="nicht_befreit">Nicht befreit</SelectItem>
                          <SelectItem value="beantragt">Beantragt</SelectItem>
                          <SelectItem value="befreit">Befreit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Gültig bis</Label>
                      <Input type="date" className="mt-1 h-8 text-xs"
                        value={form.zuzahlungsbefreiung_gueltig_bis}
                        onChange={e => setForm(p => ({ ...p, zuzahlungsbefreiung_gueltig_bis: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="nm-chroniker" checked={form.chroniker_status}
                      onChange={e => setForm(p => ({ ...p, chroniker_status: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300" />
                    <Label htmlFor="nm-chroniker" className="text-xs cursor-pointer">Chroniker-Status (1% Belastungsgrenze)</Label>
                  </div>
                </div>
              </div>
            )}

            {/* Schwerbehindertenstatus Details */}
            {isSchwerbehinderung && (
              <div className="border-t pt-4 space-y-4" data-testid="schwerbehinderung-details">
                <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Schwerbehindertenstatus
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="nm-sb-bescheid" checked={form.sb_feststellungsbescheid}
                      onChange={e => setForm(p => ({ ...p, sb_feststellungsbescheid: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300" />
                    <Label htmlFor="nm-sb-bescheid" className="text-xs cursor-pointer">Feststellungsbescheid vorhanden</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="nm-sb-ausweis" checked={form.sb_ausweis_vorhanden}
                      onChange={e => setForm(p => ({ ...p, sb_ausweis_vorhanden: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300" />
                    <Label htmlFor="nm-sb-ausweis" className="text-xs cursor-pointer">Schwerbehindertenausweis vorhanden</Label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Grad der Behinderung (GdB)</Label>
                    <Input type="number" min="0" max="100" step="10" className="mt-1 h-8 text-xs"
                      value={form.sb_gdb} placeholder="z.B. 50"
                      onChange={e => setForm(p => ({ ...p, sb_gdb: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Ausweisstatus</Label>
                    <Select value={form.sb_ausweis_status || 'none'} onValueChange={v => setForm(p => ({ ...p, sb_ausweis_status: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Status..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Status --</SelectItem>
                        <SelectItem value="gueltig">Gültig</SelectItem>
                        <SelectItem value="abgelaufen">Abgelaufen</SelectItem>
                        <SelectItem value="beantragt">Beantragt</SelectItem>
                        <SelectItem value="nicht_vorhanden">Nicht vorhanden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Ausweis gültig bis</Label>
                    <Input type="date" className="mt-1 h-8 text-xs"
                      value={form.sb_ausweis_gueltig_bis}
                      onChange={e => setForm(p => ({ ...p, sb_ausweis_gueltig_bis: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Zuständige Behörde</Label>
                    <Input className="mt-1 h-8 text-xs" value={form.sb_zustaendige_behoerde}
                      placeholder="z.B. Versorgungsamt, ZBFS, LVR"
                      onChange={e => setForm(p => ({ ...p, sb_zustaendige_behoerde: e.target.value }))} />
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Merkzeichen (kommagetrennt)</Label>
                  <Input className="mt-1 h-8 text-xs" value={form.sb_merkzeichen}
                    placeholder="z.B. G, aG, B, H, RF, Bl, Gl, 1.Kl"
                    onChange={e => setForm(p => ({ ...p, sb_merkzeichen: e.target.value }))} />
                </div>

                <div>
                  <Label className="text-xs">Ungenutzte Nachteilsausgleiche (kommagetrennt)</Label>
                  <Input className="mt-1 h-8 text-xs" value={form.sb_nachteilsausgleiche}
                    placeholder="z.B. Steuerermäßigung, Freifahrt ÖPNV, Kfz-Steuer, Parkerleichterung"
                    onChange={e => setForm(p => ({ ...p, sb_nachteilsausgleiche: e.target.value }))} />
                </div>

                <div>
                  <Label className="text-xs">Änderungsbedarf</Label>
                  <Textarea className="mt-1 text-xs" rows={2} value={form.sb_aenderungsbedarf}
                    placeholder="Was muss geändert/beantragt werden?"
                    onChange={e => setForm(p => ({ ...p, sb_aenderungsbedarf: e.target.value }))} />
                </div>
              </div>
            )}

            {/* Zuzahlungsbefreiung Details */}
            {isZuzahlungsbefreiung && (
              <div className="border-t pt-4 space-y-4" data-testid="zuzahlungsbefreiung-details">
                <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Zuzahlungsbefreiung
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="nm-zb-kk" checked={form.zb_kk_bekannt}
                      onChange={e => setForm(p => ({ ...p, zb_kk_bekannt: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300" />
                    <Label htmlFor="nm-zb-kk" className="text-xs cursor-pointer">Krankenkasse bekannt</Label>
                  </div>
                  <div>
                    <Input className="h-8 text-xs" value={form.zb_kk_name}
                      placeholder="z.B. AOK, TK, Barmer, DAK"
                      onChange={e => setForm(p => ({ ...p, zb_kk_name: e.target.value }))} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="nm-zb-antrag" checked={form.zb_antrag_gestellt}
                      onChange={e => setForm(p => ({ ...p, zb_antrag_gestellt: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300" />
                    <Label htmlFor="nm-zb-antrag" className="text-xs cursor-pointer">Antrag bereits gestellt</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="nm-zb-bewilligt" checked={form.zb_befreiung_bewilligt}
                      onChange={e => setForm(p => ({ ...p, zb_befreiung_bewilligt: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300" />
                    <Label htmlFor="nm-zb-bewilligt" className="text-xs cursor-pointer">Befreiung bewilligt</Label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Befreiung von</Label>
                    <Input type="date" className="mt-1 h-8 text-xs" value={form.zb_befreiung_von}
                      onChange={e => setForm(p => ({ ...p, zb_befreiung_von: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Befreiung bis</Label>
                    <Input type="date" className="mt-1 h-8 text-xs" value={form.zb_befreiung_bis}
                      onChange={e => setForm(p => ({ ...p, zb_befreiung_bis: e.target.value }))} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Belastungsgrenze erreicht?</Label>
                    <Select value={form.zb_belastungsgrenze || 'none'} onValueChange={v => setForm(p => ({ ...p, zb_belastungsgrenze: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Wählen --</SelectItem>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nein">Nein</SelectItem>
                        <SelectItem value="unklar">Unklar</SelectItem>
                        <SelectItem value="voraussichtlich">Voraussichtlich</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Chronische Erkrankung?</Label>
                    <Select value={form.zb_chronisch || 'none'} onValueChange={v => setForm(p => ({ ...p, zb_chronisch: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Wählen --</SelectItem>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nein">Nein</SelectItem>
                        <SelectItem value="unklar">Unklar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Nachweise vollständig?</Label>
                    <Select value={form.zb_nachweise || 'none'} onValueChange={v => setForm(p => ({ ...p, zb_nachweise: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Wählen --</SelectItem>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nein">Nein</SelectItem>
                        <SelectItem value="teilweise">Teilweise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input type="checkbox" id="nm-zb-pruef" checked={form.zb_pruefbedarf}
                    onChange={e => setForm(p => ({ ...p, zb_pruefbedarf: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300" />
                  <Label htmlFor="nm-zb-pruef" className="text-xs cursor-pointer">Weiterer Prüfbedarf</Label>
                </div>
              </div>
            )}

            {/* Notiz */}
            <div>
              <Label>Notiz</Label>
              <Textarea className={`mt-1 ${fieldStyle('notiz', kiStatus)}`} value={form.notizen}
                onChange={e => updateField('notizen', e.target.value)}
                placeholder="Weitere Informationen..." rows={2} />
            </div>

            {/* Aktionen */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={handleClose}>Abbrechen</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-neue-massnahme">
                {saving
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Speichern...</>
                  : <><Check className="w-4 h-4 mr-2" /> Massnahme anlegen</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rechte Spalte: Dokumentenvorschau */}
      {hasDocument && (
        <div className="w-80 shrink-0" data-testid="neue-massnahme-vorschau">
          <DokumentMiniVorschau
            url={params.dokumentVorschauUrl}
            name={params.dokumentName}
            dateityp={params.dokumentDateityp}
          />
        </div>
      )}
    </div>
  );
}
